import { useState } from 'react';

import { Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import Button from '@/components/button';
import Dialog from '@/components/dialog';

import Step1 from './_partials/Step1';
import Step2 from './_partials/Step2';
import Step3 from './_partials/Step3';
import { useImportCreators } from './hooks/useImportCreators';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const useStyles = makeStyles()(() => ({ stepper: { height: '72px' } }));

const ImportCreators = ({ isOpen, onClose }: Props) => {
  const { classes } = useStyles();
  const [isExitDialogOpen, setExitDialogOpen] = useState<boolean>(false);

  const {
    activeStep,
    uploadedFile,
    setUploadedFile,
    fileValidation,
    validationError,
    isValidated,
    summary,
    handleValidate,
    isBulkValidating,
    handlePrevious,
    handleContinue,
    isContinueDisabled,
    isLoading
  } = useImportCreators(onClose);

  const steps = [
    {
      title: 'Add Creator list',
      content: (
        <Step1 uploadedFile={uploadedFile} onFileChange={setUploadedFile} />
      )
    },
    {
      title: 'Verify Creator list',
      content: (
        <Step2
          fileValidation={fileValidation}
          validationError={validationError}
          isValidated={isValidated}
          onValidate={handleValidate}
          isValidating={isBulkValidating}
        />
      )
    },
    {
      title: 'Confirm Import',
      content: <Step3 summary={summary} />
    }
  ];

  const handleExitConfirm = () => {
    setExitDialogOpen(false);
    onClose();
  };

  const handleExitRequest = () => setExitDialogOpen(true);
  const handleExitCancel = () => setExitDialogOpen(false);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleExitRequest}
        title="Import list"
        fullWidth
        maxWidth="lg"
        dialogContent={
          <>
            <Stepper className={classes.stepper} activeStep={activeStep}>
              {steps.map((step) => (
                <Step key={step.title}>
                  <StepLabel>{step.title}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {steps[activeStep].content}
          </>
        }
        actions={
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Stack direction="row">
              <Button
                variant="text"
                text={activeStep > 0 ? 'previous' : 'cancel'}
                onClick={activeStep > 0 ? handlePrevious : handleExitRequest}
                disabled={isLoading}
              />
              <Button
                variant={activeStep === 2 ? 'contained' : 'text'}
                text={activeStep === 2 ? 'submit' : 'continue'}
                disabled={isContinueDisabled}
                loading={isLoading}
                onClick={handleContinue}
              />
            </Stack>
          </Stack>
        }
      />
      <Dialog
        open={isExitDialogOpen}
        title="Leaving bulk import"
        dialogContent={
          <Typography>
            Are you sure you want to leave? Your changes will not be saved.
          </Typography>
        }
        actions={
          <>
            <Button onClick={handleExitCancel} variant="text" text="cancel" />
            <Button
              id="evaluation-leave"
              onClick={handleExitConfirm}
              text="leave"
            />
          </>
        }
      />
    </>
  );
};

export default ImportCreators;
